<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useCreateApiKeyMutation, useApiKeysListQuery } from '@/composables/queries/api-keys'
import { useToast } from '@ai/ui-components/toast'
import { getUniqueApiKeyName } from '@/utils/apiKeyName'
const { t } = useI18n()
const toast = useToast()

const open = defineModel<boolean>('open', { default: false })

const keyName = ref('')
const keyNameError = ref('')
const showCancelConfirmation = ref(false)
const createdKey = ref<string | null>(null)
const isCopied = ref(false)
interface InputRef {
  $el?: HTMLElement & { querySelector?: (selector: string) => HTMLElement | null }
}

const keyNameInput = ref<InputRef>()

const keyNameValid = computed(() => keyName.value.trim().length > 0)

// Track if form has been modified
const hasUnsavedChanges = computed(() => {
  return keyName.value !== ''
})

const { mutateAsync: createApiKey, isPending: isSubmitting } = useCreateApiKeyMutation()
const { data: apiKeys } = useApiKeysListQuery()

const existingKeyNames = computed(() => (apiKeys.value ?? []).map((item) => item.name))

const handleModalClose = (newValue: boolean): void => {
  // If trying to close the modal and there are unsaved changes, show confirmation
  if (!newValue && open.value && hasUnsavedChanges.value && !createdKey.value) {
    showCancelConfirmation.value = true
    return
  }
  open.value = newValue
}

const handleCancel = (): boolean => {
  if (hasUnsavedChanges.value && !createdKey.value) {
    showCancelConfirmation.value = true
    return false
  }
  open.value = false
  return true
}

const confirmDiscard = (): boolean => {
  showCancelConfirmation.value = false
  open.value = false
  return true
}

const keepEditing = (): boolean => {
  showCancelConfirmation.value = false
  return true
}

const copyToClipboard = async () => {
  if (createdKey.value) {
    try {
      await navigator.clipboard.writeText(createdKey.value)
      isCopied.value = true
      toast.success(t('copied_to_clipboard'))
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    } catch {
      toast.error(t('failed_to_copy'), { title: t('error_occurred') })
    }
  }
}

const handleSubmit = async (): Promise<boolean> => {
  if (!keyNameValid.value) {
    keyNameError.value = t('api_key_name_required')
    return false
  }
  keyNameError.value = ''

  try {
    const trimmedName = keyName.value.trim()
    const finalName = getUniqueApiKeyName(trimmedName, existingKeyNames.value)
    const response = await createApiKey({ name: finalName })

    const key = response.data?.key

    if (key) {
      createdKey.value = key
      toast.success(t('api_key_created_successfully', [finalName]))
      return true
    } else {
      console.error('No key in response:', response)
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    }
  } catch (err: unknown) {
    console.error('Error creating API key:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    toast.error(errorMessage || t('error_occurred_try_again'), { title: t('error_occurred') })
    return false
  }
}

const handleClose = (): boolean => {
  open.value = false
  return true
}

watch(open, (val) => {
  if (!val) {
    // Reset form when modal closes
    setTimeout(() => {
      keyName.value = ''
      keyNameError.value = ''
      createdKey.value = null
      isCopied.value = false
    }, 300)
  } else if (!createdKey.value) {
    // Focus input when modal opens (only for creation form, not success screen)
    void nextTick(() => {
      const inputEl = keyNameInput.value?.$el?.querySelector('input') ?? keyNameInput.value?.$el
      if (inputEl && typeof inputEl.focus === 'function') {
        inputEl.focus()
      }
    })
  }
})
</script>

<template>
  <OModal
    v-if="!createdKey"
    :open="open"
    @update:open="handleModalClose"
    :title="t('create_api_key')"
    :secondary-action-title="t('cancel')"
    :primary-action-title="t('save')"
    :secondary-action-promise="handleCancel"
    primary-action-enabled
    primary-action-color="success"
    secondary-action-enabled
    :primary-action-promise="handleSubmit"
    :primary-action-loading="isSubmitting"
    closable
    size="medium"
    class="create-api-key-modal"
  >
    <template #content>
      <div class="flex flex-col space-y-4 pb-2 text-neutral-1">
        <span class="typography-body-2-long">{{ t('enter_api_key_name_description') }}</span>
        <div class="flex flex-col space-y-2">
          <label class="typography-body-2-strong">{{ t('name') }}</label>
          <OInput
            ref="keyNameInput"
            v-model="keyName"
            :placeholder="t('enter_api_key_name_placeholder')"
            :error-message="keyNameError"
            @keydown.enter="handleSubmit"
          />
        </div>
      </div>
    </template>
  </OModal>

  <!-- Success modal showing the created key -->
  <OModal
    v-else
    :open="open"
    @update:open="handleClose"
    :title="t('api_key_created')"
    closable
    size="medium"
  >
    <template #content>
      <div class="flex flex-col space-y-4 text-neutral-1 pb-1">
        <div class="flex items-center space-x-1">
          <UiIcon name="information" variant="solid" />
          <p class="typography-body-2-strong text-neutral-1">
            {{ t('api_key_save_warning') }}
          </p>
        </div>

        <div class="flex flex-col space-y-2">
          <div class="flex items-center space-x-2">
            <div
              v-mp-no-track
              class="flex-1 radius-l border border-neutral-4 bg-white px-4 py-2 font-mono text-sm break-all whitespace-nowrap truncate"
            >
              {{ createdKey }}
            </div>
            <OButton size="L" @click="copyToClipboard" variant="secondary" :label="t('copy')">
              <template #icon>
                <UiIcon :name="!isCopied ? 'copy' : 'positive-check'" variant="solid" />
              </template>
              <span v-if="!isCopied" class="h-5 w-5 text-neutral-2" />
            </OButton>
          </div>
        </div>
      </div>
    </template>
  </OModal>

  <!-- Unsaved changes confirmation modal -->
  <OModal
    v-model:open="showCancelConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('continue')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>
